import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
// WHY: only SignatureInput's exact output is cropped; other svgs the validator accepts may draw outside their paths
const SIGNATURE_INPUT_SVG = /^(\s*<svg xmlns="http:\/\/www\.w3\.org\/2000\/svg" viewBox=")[^"]*(">)((?:\s*<path d="[^"]+" fill="[^"]*"\s*\/>)+\s*<\/svg>\s*)$/;
const PATH_D = /<path d="([^"]+)"/g;
const SIGNATURE_PATH_DATA = /^\s*[MLQTZ][MLQTZ\d\s,.+\-eE]*$/;
const PATH_SEGMENT = /[MLQTZ][^MLQTZ]*/g;
const NUMBER = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;
const INK_PADDING = 2;
const EMPTY_BOX = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity
};
const NO_CONTROL = {
    kind: 'none'
};
const UNSUPPORTED = {
    kind: 'unsupported'
};
const parsed = (value)=>({
        kind: 'parsed',
        value
    });
const andThen = (result, next)=>result.kind === 'parsed' ? next(result.value) : UNSUPPORTED;
const chunk = (items, size)=>Array.from({
        length: items.length / size
    }, (_, i)=>items.slice(i * size, i * size + size));
const toPoints = (numbers)=>chunk(numbers, 2).map((param)=>{
        let [x, y] = param;
        return [
            x,
            y
        ];
    });
const extend = (box, points)=>points.reduce((acc, param)=>{
        let [x, y] = param;
        return {
            minX: Math.min(acc.minX, x),
            minY: Math.min(acc.minY, y),
            maxX: Math.max(acc.maxX, x),
            maxY: Math.max(acc.maxY, y)
        };
    }, box);
const toSegments = (d)=>Array.from(d.matchAll(PATH_SEGMENT), (param)=>{
        let [segment] = param;
        return {
            command: segment[0],
            numbers: Array.from(segment.slice(1).matchAll(NUMBER), (param)=>{
                let [n] = param;
                return Number(n);
            })
        };
    });
const walkQuadratic = (walk, numbers)=>chunk(numbers, 4).reduce((acc, param)=>{
        let [cx, cy, x, y] = param;
        return _object_spread_props(_object_spread({}, acc), {
            current: [
                x,
                y
            ],
            lastControl: {
                kind: 'control',
                point: [
                    cx,
                    cy
                ]
            },
            box: extend(acc.box, [
                [
                    cx,
                    cy
                ],
                [
                    x,
                    y
                ]
            ])
        });
    }, walk);
// WHY: a T curve bends toward the previous control point reflected about the current point, which the path never lists
const walkSmoothQuadratic = (walk, numbers)=>toPoints(numbers).reduce((acc, end)=>{
        const control = acc.lastControl.kind === 'control' ? [
            2 * acc.current[0] - acc.lastControl.point[0],
            2 * acc.current[1] - acc.lastControl.point[1]
        ] : acc.current;
        return _object_spread_props(_object_spread({}, acc), {
            current: end,
            lastControl: {
                kind: 'control',
                point: control
            },
            box: extend(acc.box, [
                control,
                end
            ])
        });
    }, walk);
const walkLine = (walk, points)=>_object_spread_props(_object_spread({}, walk), {
        current: points[points.length - 1],
        lastControl: NO_CONTROL,
        box: extend(walk.box, points)
    });
const walkSegment = (walk, param)=>{
    let { command, numbers } = param;
    const hasPairs = numbers.length > 0 && numbers.length % 2 === 0;
    switch(command){
        case 'M':
            return hasPairs ? parsed(walkLine(_object_spread_props(_object_spread({}, walk), {
                subpathStart: [
                    numbers[0],
                    numbers[1]
                ]
            }), toPoints(numbers))) : UNSUPPORTED;
        case 'L':
            return hasPairs ? parsed(walkLine(walk, toPoints(numbers))) : UNSUPPORTED;
        case 'Q':
            return numbers.length > 0 && numbers.length % 4 === 0 ? parsed(walkQuadratic(walk, numbers)) : UNSUPPORTED;
        case 'T':
            return hasPairs ? parsed(walkSmoothQuadratic(walk, numbers)) : UNSUPPORTED;
        case 'Z':
            return numbers.length === 0 ? parsed(_object_spread_props(_object_spread({}, walk), {
                current: walk.subpathStart,
                lastControl: NO_CONTROL
            })) : UNSUPPORTED;
        default:
            return UNSUPPORTED;
    }
};
const pathBox = (d)=>{
    const walked = SIGNATURE_PATH_DATA.test(d) ? toSegments(d).reduce((walk, segment)=>andThen(walk, (state)=>walkSegment(state, segment)), parsed({
        current: [
            0,
            0
        ],
        subpathStart: [
            0,
            0
        ],
        lastControl: NO_CONTROL,
        box: EMPTY_BOX
    })) : UNSUPPORTED;
    return andThen(walked, (walk)=>parsed(walk.box));
};
const inkBox = (content)=>{
    const signature = SIGNATURE_INPUT_SVG.exec(content);
    const box = signature != null ? Array.from(signature[3].matchAll(PATH_D), (param)=>{
        let [, d] = param;
        return d;
    }).reduce((acc, d)=>andThen(acc, (merged)=>andThen(pathBox(d), (path)=>parsed(extend(merged, [
                    [
                        path.minX,
                        path.minY
                    ],
                    [
                        path.maxX,
                        path.maxY
                    ]
                ])))), parsed(EMPTY_BOX)) : UNSUPPORTED;
    return andThen(box, (found)=>[
            found.minX,
            found.minY,
            found.maxX,
            found.maxY
        ].every(Number.isFinite) ? parsed(found) : UNSUPPORTED);
};
const toViewBox = (param)=>{
    let { minX, minY, maxX, maxY } = param;
    return [
        minX - INK_PADDING,
        minY - INK_PADDING,
        maxX - minX + 2 * INK_PADDING,
        maxY - minY + 2 * INK_PADDING
    ].map((n)=>Number(n.toFixed(2))).join(' ');
};
export const cropSignatureSvgToInk = (content)=>{
    const box = inkBox(content);
    return box.kind === 'parsed' ? content.replace(SIGNATURE_INPUT_SVG, (_, open, close, body)=>"".concat(open).concat(toViewBox(box.value)).concat(close).concat(body)) : content;
};
